import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, ChevronDown, ChevronUp, Check, AlertCircle, Clock,
  DollarSign, Percent, Calendar, Shield, TrendingUp, Building2, Zap
} from "lucide-react";

/* ── Currency formatter ── */
function fmtUSD(v) {
  if (v == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);
}

/* ── Highlight best value ── */
function bestIndex(values, direction = "low") {
  const nums = values.map(v => (typeof v === "number" ? v : null));
  const filtered = nums.filter(n => n !== null);
  if (filtered.length < 2) return -1;
  const target = direction === "low" ? Math.min(...filtered) : Math.max(...filtered);
  return nums.indexOf(target);
}

/* ── BORROWER: Compare Term Sheets ── */
export function TermSheetComparison({ termSheets, onClose, onSelect }) {
  const [expanded, setExpanded] = useState({});

  if (!termSheets || termSheets.length < 2) {
    return (
      <div className="rounded-2xl bg-white border border-[#EDE8DC] p-8 text-center" data-testid="comparison-empty">
        <AlertCircle className="h-10 w-10 text-[#ddd] mx-auto mb-3" />
        <h3 className="text-sm font-bold text-[#333]">Need at least 2 term sheets to compare</h3>
        <p className="text-xs text-[#888] mt-1">Check back when more lenders submit offers.</p>
      </div>
    );
  }

  const sheets = termSheets.slice(0, 4);

  /* ── Calculate derived values ── */
  const enriched = sheets.map(ts => {
    const rate = ts.interest_rate || ts.rate || 0;
    const amt = ts.loan_amount || 0;
    const term = ts.term_months || 12;
    const pts = ts.points || ts.origination_fee_pct || 0;
    const monthlyRate = rate / 100 / 12;
    const monthlyPayment = monthlyRate > 0 ? (amt * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -term)) : amt / term;
    const totalInterest = (monthlyPayment * term) - amt;
    const pointsCost = (pts / 100) * amt;
    const totalCost = totalInterest + pointsCost;
    return { ...ts, _rate: rate, _amt: amt, _term: term, _pts: pts, _monthly: monthlyPayment, _totalInterest: totalInterest, _pointsCost: pointsCost, _totalCost: totalCost };
  });

  const rows = [
    { label: "Interest Rate", key: "_rate", fmt: v => `${v}%`, best: "low", icon: Percent },
    { label: "Loan Amount", key: "_amt", fmt: v => fmtUSD(v), best: "high", icon: DollarSign },
    { label: "Points / Origination", key: "_pts", fmt: v => `${v} pts`, best: "low", icon: DollarSign },
    { label: "Term", key: "_term", fmt: v => `${v} months`, best: "high", icon: Calendar },
    { label: "Closing Timeline", key: "closing_days", fmt: v => v ? `${v} days` : "—", best: "low", icon: Clock },
    { label: "Est. Monthly Payment", key: "_monthly", fmt: v => fmtUSD(v), best: "low", icon: DollarSign, highlight: true },
    { label: "Total Interest Cost", key: "_totalInterest", fmt: v => fmtUSD(v), best: "low", icon: TrendingUp },
    { label: "Points Cost", key: "_pointsCost", fmt: v => fmtUSD(v), best: "low", icon: DollarSign },
    { label: "Total Cost of Loan", key: "_totalCost", fmt: v => fmtUSD(v), best: "low", icon: Zap, highlight: true },
    { label: "Prepayment Penalty", key: "prepayment_penalty", fmt: v => v || "None", best: null, icon: AlertCircle },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
      className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.08)]"
      data-testid="term-sheet-comparison"
    >
      {/* Header */}
      <div className="px-5 py-4 bg-gradient-to-r from-[#064e3b] to-emerald-800 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-white tracking-tight">Term Sheet Comparison</h2>
          <p className="text-[10px] text-emerald-200 mt-0.5">{sheets.length} offers · Best values highlighted in green</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="comparison-close">
            <X className="h-4 w-4 text-white/70" />
          </button>
        )}
      </div>

      {/* Lender Headers */}
      <div className="grid border-b border-[#EDE8DC]" style={{ gridTemplateColumns: `180px repeat(${sheets.length}, 1fr)` }}>
        <div className="px-4 py-3 bg-[#FDFCF9] border-r border-[#EDE8DC]">
          <span className="text-[9px] font-bold text-[#999] uppercase tracking-[0.15em]">Metric</span>
        </div>
        {enriched.map((ts, i) => (
          <div key={ts.ts_id || i} className={`px-4 py-3 text-center ${i < sheets.length - 1 ? "border-r border-[#EDE8DC]" : ""}`}>
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <div className="w-7 h-7 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                <Building2 className="h-3.5 w-3.5 text-emerald-700" />
              </div>
            </div>
            <div className="text-xs font-bold text-[#0A0A0A]">{ts.lender_name || `Lender ${String.fromCharCode(65 + i)}`}</div>
            {ts.status && (
              <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[8px] font-bold ${
                ts.status === "selected" ? "bg-emerald-100 text-emerald-700" :
                ts.status === "submitted" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"
              }`}>{ts.status}</span>
            )}
          </div>
        ))}
      </div>

      {/* Data Rows */}
      <div className="divide-y divide-[#f5f0e8]">
        {rows.map((row) => {
          const values = enriched.map(ts => ts[row.key]);
          const numValues = values.map(v => (typeof v === "number" ? v : null));
          const best = row.best ? bestIndex(numValues, row.best) : -1;

          return (
            <div key={row.label} className={`grid ${row.highlight ? "bg-[#FDFCF9]" : ""}`}
              style={{ gridTemplateColumns: `180px repeat(${sheets.length}, 1fr)` }}
              data-testid={`comparison-row-${row.key}`}>
              <div className="px-4 py-3 flex items-center gap-2 border-r border-[#EDE8DC]">
                <row.icon className="h-3.5 w-3.5 text-[#bbb] shrink-0" />
                <span className={`text-xs ${row.highlight ? "font-bold text-[#0A0A0A]" : "font-medium text-[#555]"}`}>{row.label}</span>
              </div>
              {enriched.map((ts, i) => (
                <div key={ts.ts_id || i}
                  className={`px-4 py-3 text-center ${i < sheets.length - 1 ? "border-r border-[#EDE8DC]" : ""} ${best === i ? "bg-emerald-50/60" : ""}`}>
                  <span className={`text-sm tabular-nums ${
                    best === i ? "font-bold text-emerald-700" : row.highlight ? "font-bold text-[#0A0A0A]" : "text-[#333]"
                  }`}>
                    {row.fmt(values[i])}
                  </span>
                  {best === i && <Check className="h-3 w-3 text-emerald-500 inline ml-1" />}
                </div>
              ))}
            </div>
          );
        })}

        {/* Conditions row (expandable) */}
        <div>
          <button
            onClick={() => setExpanded(prev => ({ ...prev, conditions: !prev.conditions }))}
            className="w-full grid px-0" style={{ gridTemplateColumns: `180px 1fr` }}
            data-testid="comparison-toggle-conditions"
          >
            <div className="px-4 py-3 flex items-center gap-2 border-r border-[#EDE8DC]">
              <AlertCircle className="h-3.5 w-3.5 text-[#bbb] shrink-0" />
              <span className="text-xs font-medium text-[#555]">Conditions</span>
              {expanded.conditions ? <ChevronUp className="h-3 w-3 text-[#bbb] ml-auto" /> : <ChevronDown className="h-3 w-3 text-[#bbb] ml-auto" />}
            </div>
            <div className="px-4 py-3 text-left">
              <span className="text-[10px] text-[#999]">{expanded.conditions ? "Hide" : "Show"} lender conditions</span>
            </div>
          </button>
          <AnimatePresence>
            {expanded.conditions && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                className="grid overflow-hidden" style={{ gridTemplateColumns: `180px repeat(${sheets.length}, 1fr)` }}>
                <div className="px-4 py-3 border-r border-[#EDE8DC] bg-amber-50/30" />
                {enriched.map((ts, i) => (
                  <div key={ts.ts_id || i} className={`px-4 py-3 bg-amber-50/30 ${i < sheets.length - 1 ? "border-r border-[#EDE8DC]" : ""}`}>
                    <p className="text-[11px] text-[#555] leading-relaxed">{ts.conditions || "No special conditions"}</p>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Action Footer */}
      {onSelect && (
        <div className="px-5 py-4 bg-[#FDFCF9] border-t border-[#EDE8DC] flex items-center justify-end gap-3">
          <span className="text-[10px] text-[#888] mr-auto">Select the best offer for your deal</span>
          {enriched.map((ts, i) => (
            <button key={ts.ts_id || i} onClick={() => onSelect(ts)}
              disabled={ts.status === "selected"}
              data-testid={`comparison-select-${i}`}
              className="px-4 py-2 rounded-xl text-xs font-semibold bg-emerald-700 text-white hover:bg-emerald-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {ts.status === "selected" ? "Selected" : `Select ${ts.lender_name?.split(" ")[0] || `Lender ${String.fromCharCode(65 + i)}`}`}
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
}


/* ── LENDER: Compare Deals ── */
export function DealComparison({ deals, onClose, onViewDeal }) {
  if (!deals || deals.length < 2) {
    return (
      <div className="rounded-2xl bg-white border border-[#EDE8DC] p-8 text-center" data-testid="deal-comparison-empty">
        <AlertCircle className="h-10 w-10 text-[#ddd] mx-auto mb-3" />
        <h3 className="text-sm font-bold text-[#333]">Select at least 2 deals to compare</h3>
        <p className="text-xs text-[#888] mt-1">Check the boxes on deal cards, then click Compare.</p>
      </div>
    );
  }

  const items = deals.slice(0, 4);

  const rows = [
    { label: "Deal Number", key: "deal_number", fmt: v => v || "—", best: null, icon: Building2 },
    { label: "Loan Type", key: "loan_type", fmt: v => ({ fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", construction: "Construction", commercial: "Commercial", ground_up: "Ground Up" }[v] || v || "—"), best: null, icon: Building2 },
    { label: "Purchase Price", key: "purchase_price", fmt: v => fmtUSD(v), best: null, icon: DollarSign },
    { label: "Loan Amount", key: "requested_loan_amount", fmt: v => fmtUSD(v), best: "high", icon: DollarSign, highlight: true },
    { label: "ARV", key: "arv", fmt: v => fmtUSD(v), best: "high", icon: TrendingUp },
    { label: "LTV", key: d => d.requested_loan_amount && d.purchase_price ? `${((d.requested_loan_amount / d.purchase_price) * 100).toFixed(1)}%` : "—", best: null, icon: Percent, highlight: true },
    { label: "FICO Score", key: d => d.fico_score || d.summary?.fico_score || "—", best: "high", icon: Shield },
    { label: "Risk Score", key: d => d.summary?.risk_score ?? "—", best: "low", icon: AlertCircle },
    { label: "Borrower Experience", key: d => d.borrower_experience || d.summary?.borrower_experience || "—", best: null, icon: Shield },
    { label: "Exit Strategy", key: d => d.exit_strategy || "—", best: null, icon: TrendingUp },
    { label: "Stage", key: "stage", fmt: v => v || "—", best: null, icon: Clock },
    { label: "Lender Interest", key: "total_interest", fmt: v => v || 0, best: "high", icon: Zap },
    { label: "Property Address", key: "property_address", fmt: v => v || "—", best: null, icon: Building2 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
      className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.08)]"
      data-testid="deal-comparison"
    >
      {/* Header */}
      <div className="px-5 py-4 bg-gradient-to-r from-[#064e3b] to-emerald-800 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-white tracking-tight">Deal Comparison</h2>
          <p className="text-[10px] text-emerald-200 mt-0.5">{items.length} deals · Best values highlighted in green</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="deal-comparison-close">
            <X className="h-4 w-4 text-white/70" />
          </button>
        )}
      </div>

      {/* Deal Headers */}
      <div className="grid border-b border-[#EDE8DC]" style={{ gridTemplateColumns: `180px repeat(${items.length}, 1fr)` }}>
        <div className="px-4 py-3 bg-[#FDFCF9] border-r border-[#EDE8DC]">
          <span className="text-[9px] font-bold text-[#999] uppercase tracking-[0.15em]">Metric</span>
        </div>
        {items.map((deal, i) => (
          <div key={deal.lead_id || i} className={`px-4 py-3 text-center ${i < items.length - 1 ? "border-r border-[#EDE8DC]" : ""}`}>
            <div className="text-xs font-bold text-[#0A0A0A]">{deal.deal_number || deal.property_address?.substring(0, 18) || `Deal ${i + 1}`}</div>
            <div className="text-[9px] text-[#888] mt-0.5">{deal.loan_type || ""}</div>
          </div>
        ))}
      </div>

      {/* Data Rows */}
      <div className="divide-y divide-[#f5f0e8]">
        {rows.map((row) => {
          const values = items.map(deal => {
            if (typeof row.key === "function") return row.key(deal);
            return deal[row.key];
          });
          const numValues = values.map(v => (typeof v === "number" ? v : null));
          const best = row.best ? bestIndex(numValues, row.best) : -1;

          return (
            <div key={row.label} className={`grid ${row.highlight ? "bg-[#FDFCF9]" : ""}`}
              style={{ gridTemplateColumns: `180px repeat(${items.length}, 1fr)` }}
              data-testid={`deal-comparison-row-${row.label.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="px-4 py-3 flex items-center gap-2 border-r border-[#EDE8DC]">
                <row.icon className="h-3.5 w-3.5 text-[#bbb] shrink-0" />
                <span className={`text-xs ${row.highlight ? "font-bold text-[#0A0A0A]" : "font-medium text-[#555]"}`}>{row.label}</span>
              </div>
              {items.map((deal, i) => {
                const val = values[i];
                const formatted = row.fmt ? row.fmt(val) : (val ?? "—");
                return (
                  <div key={deal.lead_id || i}
                    className={`px-4 py-3 text-center ${i < items.length - 1 ? "border-r border-[#EDE8DC]" : ""} ${best === i ? "bg-emerald-50/60" : ""}`}>
                    <span className={`text-sm tabular-nums ${
                      best === i ? "font-bold text-emerald-700" : row.highlight ? "font-bold text-[#0A0A0A]" : "text-[#333]"
                    }`}>
                      {formatted}
                    </span>
                    {best === i && <Check className="h-3 w-3 text-emerald-500 inline ml-1" />}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Action Footer */}
      {onViewDeal && (
        <div className="px-5 py-4 bg-[#FDFCF9] border-t border-[#EDE8DC] flex items-center justify-end gap-3">
          <span className="text-[10px] text-[#888] mr-auto">Click to view full deal details</span>
          {items.map((deal, i) => (
            <button key={deal.lead_id || i} onClick={() => onViewDeal(deal.lead_id)}
              data-testid={`deal-comparison-view-${i}`}
              className="px-4 py-2 rounded-xl text-xs font-semibold bg-[#064e3b] text-white hover:bg-emerald-700 transition-colors">
              View {deal.deal_number || `Deal ${i + 1}`}
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
}
